<template>
  <div class="skill-detail-page">
    <a-spin :spinning="loading">
      <div v-if="skill" class="detail-content">
        <div class="detail-header">
          <div class="title-row">
            <h2>{{ skill.name }}</h2>
            <a-tag color="green">v{{ skill.latestVersion }}</a-tag>
            <a-tag>{{ categoryText(skill.category) }}</a-tag>
          </div>
          <p class="summary">{{ skill.summary }}</p>
          <div class="meta-row">
            <span>更新时间：{{ skill.updatedAt || '-' }}</span>
            <span>大小：{{ formatSize(skill.packageSize) }}</span>
            <span>下载次数：{{ skill.downloadCount || 0 }}</span>
          </div>
          <a-space class="action-row">
            <a-button type="primary" :loading="promptLoading" @click="copyPrompt">
              <template #icon><CopyOutlined /></template>
              一键安装
            </a-button>
            <a-button :href="downloadUrl(currentVersion)" target="_blank">
              <template #icon><DownloadOutlined /></template>
              下载Skill包
            </a-button>
          </a-space>
        </div>

        <a-tabs v-model:activeKey="activeTab">
          <a-tab-pane key="intro" tab="技能介绍">
            <div class="document-block">{{ skill.descriptionMd || skill.summary }}</div>
          </a-tab-pane>

          <a-tab-pane key="install" tab="安装方式">
            <div class="install-layout">
              <div class="platform-panel">
                <a-radio-group v-model:value="selectedAgent" button-style="solid">
                  <a-radio-button v-for="agent in skill.supportedAgents || []" :key="agent" :value="agent">
                    {{ agentText(agent) }}
                  </a-radio-button>
                </a-radio-group>
                <p class="platform-tip">{{ platformTip }}</p>
                <a-alert
                  v-if="securityWarnings.length"
                  type="warning"
                  show-icon
                  message="该Skill包包含脚本文件"
                  :description="securityWarnings.join('；')"
                />
              </div>
              <div class="prompt-panel">
                <div class="panel-header">
                  <span>安装Prompt</span>
                  <a-button size="small" type="primary" :loading="promptLoading" @click="copyPrompt">复制</a-button>
                </div>
                <pre>{{ installPrompt || '点击一键安装后会生成30分钟内有效的安装Prompt，其中包含智能体可直接下载的Skill包地址。' }}</pre>
              </div>
            </div>
          </a-tab-pane>

          <a-tab-pane key="versions" tab="版本记录">
            <a-table :columns="versionColumns" :data-source="skill.versions || []" row-key="id" :pagination="false">
              <template #status="{ record }">
                <a-tag :color="record.status === 'published' ? 'green' : record.status === 'draft' ? 'orange' : 'red'">
                  {{ statusText(record.status) }}
                </a-tag>
              </template>
              <template #size="{ record }">{{ formatSize(record.packageSize) }}</template>
              <template #warnings="{ record }">{{ (record.securityWarnings || []).join('；') || '-' }}</template>
              <template #action="{ record }">
                <a-space>
                  <a-button
                    v-if="record.status === 'published'"
                    size="small"
                    type="primary"
                    :loading="promptLoading && activePromptVersionId === record.id"
                    @click="copyVersionPrompt(record)"
                  >
                    复制安装
                  </a-button>
                  <a-button
                    v-if="record.status === 'published'"
                    size="small"
                    :href="downloadUrl(record)"
                    target="_blank"
                  >
                    下载
                  </a-button>
                </a-space>
              </template>
            </a-table>
          </a-tab-pane>
        </a-tabs>
      </div>
    </a-spin>
  </div>
</template>

<script>
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { downloadSkill, getInstallPrompt, getSkillDetail } from '@/api/skill';

export default {
  components: { CopyOutlined, DownloadOutlined },
  props: {
    skillId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      detailRequestId: 0,
      promptLoading: false,
      promptRequestId: 0,
      activePromptVersionId: '',
      skill: null,
      installPrompt: '',
      selectedAgent: '',
      activeTab: 'intro',
      versionColumns: [
        { title: '版本', dataIndex: 'version', key: 'version', width: 100 },
        { title: '状态', dataIndex: 'status', key: 'status', width: 100, slots: { customRender: 'status' } },
        { title: '大小', dataIndex: 'packageSize', key: 'size', width: 100, slots: { customRender: 'size' } },
        { title: '发布时间', dataIndex: 'publishedAt', key: 'publishedAt', width: 170 },
        { title: '安全提示', dataIndex: 'securityWarnings', key: 'warnings', slots: { customRender: 'warnings' } },
        { title: '操作', key: 'action', width: 180, slots: { customRender: 'action' } },
      ],
      categories: [
        { label: '通用', value: 'general' },
        { label: '投放查询', value: 'advertising' },
        { label: '数据分析', value: 'data' },
        { label: '内容创作', value: 'content' },
        { label: '办公效率', value: 'productivity' },
      ],
      agents: {
        codex: 'Codex',
        claude: 'Claude',
        openclaw: 'OpenClaw',
        workbuddy: 'WorkBuddy',
        hermes: 'Hermes',
      },
    };
  },
  computed: {
    currentVersion() {
      return (this.skill?.versions || []).find(item => item.id === this.skill.latestVersionId);
    },
    securityWarnings() {
      return this.currentVersion?.securityWarnings || [];
    },
    platformTip() {
      const tips = {
        codex: '打开Codex对话，粘贴安装Prompt并发送，Codex会自动下载并安装。',
        claude: '打开Claude对话，粘贴安装Prompt并发送，Claude会自动下载并安装。',
        openclaw: '打开OpenClaw对话，粘贴安装Prompt并发送，OpenClaw会自动下载并安装。',
        workbuddy: '打开WorkBuddy对话，粘贴安装Prompt并发送，WorkBuddy会自动下载并安装。',
        hermes: '打开Hermes对话，粘贴安装Prompt并发送，Hermes会自动下载并安装。',
      };
      return tips[this.selectedAgent] || '选择平台后，复制安装Prompt并发送到对应智能体即可。';
    },
  },
  watch: {
    skillId: {
      immediate: true,
      handler() {
        this.loadDetail();
      },
    },
  },
  methods: {
    async loadDetail() {
      const requestId = ++this.detailRequestId;
      const skillId = this.skillId;
      this.skill = null;
      this.installPrompt = '';
      this.promptLoading = false;
      this.promptRequestId += 1;
      this.activeTab = 'intro';
      this.selectedAgent = '';
      this.loading = true;
      try {
        const res = await getSkillDetail(skillId);
        if (requestId !== this.detailRequestId) return;
        if (res.code === 0) {
          this.skill = res.data;
          this.selectedAgent = this.skill.supportedAgents?.[0] || 'codex';
        }
      } finally {
        if (requestId === this.detailRequestId) this.loading = false;
      }
    },
    async copyPrompt() {
      if (this.currentVersion) {
        await this.copyVersionPrompt(this.currentVersion);
      }
    },
    async copyVersionPrompt(version) {
      if (!version || this.promptLoading) return;
      const requestId = ++this.promptRequestId;
      this.promptLoading = true;
      this.activePromptVersionId = version.id;
      try {
        const promptRes = await getInstallPrompt(
          version.id,
          `${window.location.origin}${process.env.VUE_APP_BASE_URL}`
        );
        if (promptRes.code !== 0) return;
        const isCurrentVersion = (this.skill?.versions || []).some(item => item.id === version.id);
        if (requestId !== this.promptRequestId || !isCurrentVersion) return;
        this.installPrompt = promptRes.data || '';
        await navigator.clipboard.writeText(this.installPrompt);
        message.success('安装Prompt已复制');
      } catch {
        const textarea = document.createElement('textarea');
        textarea.value = this.installPrompt;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        message.success('安装Prompt已复制');
      } finally {
        if (requestId === this.promptRequestId) {
          this.promptLoading = false;
          this.activePromptVersionId = '';
        }
      }
    },
    downloadUrl(version) {
      return version ? downloadSkill(version.id) : '';
    },
    categoryText(value) {
      return this.categories.find(item => item.value === value)?.label || value || '通用';
    },
    agentText(value) {
      return this.agents[value] || value;
    },
    statusText(value) {
      return { draft: '草稿', published: '已发布', disabled: '已下架' }[value] || value;
    },
    formatSize(value) {
      if (!value) return '-';
      if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
      return `${(value / 1024 / 1024).toFixed(1)} MB`;
    },
  },
};
</script>

<style lang="less" scoped>
.skill-detail-page {
  min-height: 100%;
}

.detail-content {
  padding: 0 4px 20px 0;
}

.detail-header {
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 20px;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;

  h2 {
    margin: 0;
    color: #111827;
    font-size: 24px;
  }
}

.summary {
  margin: 10px 0 0;
  color: #4b5563;
}

.meta-row {
  display: flex;
  gap: 24px;
  margin-top: 12px;
  color: #6b7280;
  font-size: 13px;
}

.action-row {
  margin-top: 18px;
}

.document-block {
  min-height: 180px;
  color: #374151;
  line-height: 1.8;
  white-space: pre-wrap;
}

.install-layout {
  display: grid;
  grid-template-columns: minmax(260px, 360px) 1fr;
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}

.platform-panel {
  .platform-tip {
    margin: 16px 0;
    color: #374151;
    line-height: 1.7;
  }
}

.prompt-panel {
  min-width: 0;
  border: 1px solid #d1d5db;
  border-radius: 6px;

  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid #e5e7eb;
    color: #374151;
    font-weight: 600;
  }

  pre {
    max-height: 360px;
    margin: 0;
    padding: 14px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
    color: #111827;
    font-size: 13px;
    line-height: 1.7;
  }
}
</style>
